<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { computed, ref } from "vue";
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import { cnmcApi } from "./cnmcApi";
import { cnmcOutboundApi } from "./cnmcOutboundApi";
import type {
  CnmcFilingView,
  OutboundFiling,
  OutboundMonthCount,
} from "./cnmcTypes";
import CnmcApplicationCard from "./CnmcApplicationCard.vue";
import CnmcOutboundFacts from "./CnmcOutboundFacts.vue";
import CnmcOutboundRowsTable from "./CnmcOutboundRowsTable.vue";
import CnmcNotice from "./CnmcNotice.vue";
import { formatCnmcMonthCount } from "./cnmcPresentation";
import { useCnmcApplicationActions } from "./useCnmcApplicationActions";

/**
 * A transfer or export prepared from synced CNMC holdings: who files it, where it goes, what CNMC is
 * asked to move and has debited, and its filing card. Discarding an unsubmitted file and closing a
 * rejected or withdrawn one both release the reservation; a submitted filing can only be withdrawn
 * at the CNMC Sede (Desistimiento).
 */
const props = withDefaults(
  defineProps<{
    companyId: number;
    filing: OutboundFiling;
    canReview?: boolean;
    isAdmin?: boolean;
  }>(),
  { canReview: false, isAdmin: false }
);
const emit = defineEmits<{ update: [filing: OutboundFiling] }>();

const { t, locale } = useI18n();
const confirmingDiscard = ref(false);

const update = (view: CnmcFilingView) => {
  if (view.kind === "OUTBOUND") emit("update", view.outbound);
};
// Reloaded by order rather than by application: a replacement files a new application for it.
const { busy, perform, applicationAction } = useCnmcApplicationActions({
  companyId: () => props.companyId,
  connectionId: () => props.filing.connection_id,
  update,
  refresh: async () =>
    update(
      (await cnmcApi.order(props.companyId, props.filing.order_id)).data.filing
    ),
});

const discardLabelKey = computed(() =>
  props.filing.next_action === "CLOSE"
    ? "cnmc.outbound.summary.close"
    : "cnmc.outbound.summary.discard"
);
const showWithdrawHelp = computed(
  () =>
    !props.filing.can_abandon &&
    ["WAIT_CNMC", "WAIT_ACKNOWLEDGEMENTS"].includes(props.filing.next_action)
);

const formatMonths = (months: OutboundMonthCount[]) =>
  months.map((month) => formatCnmcMonthCount(month, locale.value)).join(", ");

const abandon = () =>
  perform(async () => {
    confirmingDiscard.value = false;
    const { data } = await cnmcOutboundApi.abandon(
      props.companyId,
      props.filing.connection_id,
      props.filing.application.id
    );
    emit("update", data);
  });
</script>

<template>
  <section class="d-flex flex-column gap-3" data-testid="cnmc-outbound-summary">
    <div class="d-flex justify-content-between align-items-start gap-2">
      <div>
        <h6 class="mb-1">{{ t(`cnmc.outbound.routes.${filing.route}`) }}</h6>
        <div class="small text-body-secondary">
          {{ t(`cnmc.outbound.next_actions.${filing.next_action}`) }}
        </div>
      </div>
    </div>

    <CnmcOutboundFacts
      :holder="filing.holder"
      :recipient="filing.recipient"
      :destination="filing.destination"
      :totalMwh="filing.total_mwh"
      :filingDeadline="filing.filing_deadline"
    />

    <CnmcNotice v-if="filing.released">
      {{ t("cnmc.outbound.summary.released") }}
    </CnmcNotice>
    <CnmcNotice v-if="filing.shortfall_go_count > 0">
      {{
        t("cnmc.outbound.summary.shortfall", {
          count: formatNumberWithSpaces(filing.shortfall_go_count),
        })
      }}
    </CnmcNotice>
    <CnmcNotice
      v-if="
        filing.application.deadline_passed && filing.next_action === 'SUBMIT'
      "
    >
      {{ t("cnmc.outbound.summary.deadline_passed") }}
    </CnmcNotice>

    <CnmcOutboundRowsTable :rows="filing.sources">
      <template #headers>
        <th scope="col">
          {{ t("cnmc.outbound.summary.selected_months") }}
        </th>
        <th scope="col">
          {{ t("cnmc.outbound.summary.delivered_months") }}
        </th>
      </template>
      <template #cells="{ row }">
        <td class="small">{{ formatMonths(row.selected_months) }}</td>
        <td class="small">
          {{ formatMonths(row.delivered_months) || "—" }}
        </td>
      </template>
    </CnmcOutboundRowsTable>

    <CnmcApplicationCard
      :application="filing.application"
      :busy
      :canPrepare="filing.can_replace"
      :canReview
      :isAdmin
      @action="applicationAction(filing.application, $event)"
    />

    <p v-if="showWithdrawHelp" class="small text-body-secondary mb-0">
      {{ t("cnmc.outbound.summary.withdraw_help") }}
    </p>

    <div v-if="filing.can_abandon" class="d-flex flex-wrap gap-2">
      <template v-if="confirmingDiscard">
        <span class="small align-self-center">
          {{ t("cnmc.outbound.summary.discard_confirm") }}
        </span>
        <button
          type="button"
          class="btn btn-sm btn-danger"
          :disabled="busy"
          @click="abandon"
        >
          {{ t(discardLabelKey) }}
        </button>
        <button
          type="button"
          class="btn btn-sm btn-ghost"
          :disabled="busy"
          @click="confirmingDiscard = false"
        >
          {{ t("common.back") }}
        </button>
      </template>
      <button
        v-else
        type="button"
        class="btn btn-sm btn-outline-danger"
        :disabled="busy"
        @click="confirmingDiscard = true"
      >
        {{ t(discardLabelKey) }}
      </button>
    </div>
  </section>
</template>
